import { Package, ShoppingCart, Truck } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import { setCartTotalQuantity } from '~/redux/reducers/cart';
import type { RootState } from '~/redux/store';
import { useEffect } from 'react';
import { formatCurrency } from '~/utils';
import { calcItemLoads } from '~/utils/order';

export type CartSummaryProps = {
  targetSize: number;
};

export function CartSummary({ targetSize }: Readonly<CartSummaryProps>) {
  const dispatch = useDispatch();
  const { t } = useTranslation(['order-builder', 'cart']);

  const { carts } = useSelector((state: RootState) => state.cart);
  const { inventory } = useSelector((state: RootState) => state.inventory);

  const usageRatePerWeek =
    inventory?.buyerInventoryConfiguration?.usageRatePerWeek?.value || 0;
  let totalQuantity = 0;
  let totalValue = 0;
  let totalCount = 0;
  let totalTruckLoads = 0;
  let totalRailcarLoads = 0;

  carts.forEach((cart) =>
    cart.items?.forEach((item) => {
      totalQuantity += item.quantity.value;
      totalValue += item.quantity.value * item.price;
      totalCount += 1;
      const loads = calcItemLoads(item);
      totalTruckLoads += loads.truckLoads;
      totalRailcarLoads += loads.railcarLoads;
    })
  );

  useEffect(() => {
    dispatch(setCartTotalQuantity(totalQuantity));
  }, [dispatch, totalQuantity]);

  const averagePrice = totalCount > 0 ? totalValue / totalQuantity : 0;
  const unit = 'N/A';

  const weeksOfSupply = parseFloat(
    (totalQuantity / (usageRatePerWeek || 1)).toFixed(2)
  );
  const targetWeeksOfSupply = parseFloat(
    (targetSize / (usageRatePerWeek || 1)).toFixed(2)
  );

  return (
    <div className="px-6 py-4 space-y-4">
      <div className="space-y-3">
        <div className="flex items-center justify-between text-sm pt-2">
          <span className="text-neut-sec">
            {t('order_builder.cart_summary.total_value')}
          </span>
          <span
            data-testid="summary-total-value"
            className="font-semibold text-neut-900"
          >
            {formatCurrency(totalValue)}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-neut-sec">
            {t('order_builder.cart_summary.avg_price')}
          </span>
          <span
            data-testid="summary-average-price"
            className="font-semibold text-success-600"
          >
            ${averagePrice.toLocaleString()}/{unit}
          </span>
        </div>
      </div>
      <QuantityCard
        totalQuantity={totalQuantity}
        targetSize={targetSize}
        unit={unit}
        totalLabel={t('order_builder.cart_summary.total_quantity')}
        targetLabel={t('order_builder.cart_summary.target_size')}
      />
      <QuantityCard
        totalQuantity={weeksOfSupply}
        targetSize={targetWeeksOfSupply}
        unit={t('order_builder.order_calculator.units.weeks')}
        totalLabel={t('order_builder.cart_summary.actual_weeks_cover')}
        targetLabel={t('order_builder.cart_summary.target_weeks')}
      />
      {totalCount > 0 && (
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 px-2 py-1 bg-status-info-50 rounded-md">
            <Truck
              size={10}
              className="text-status-info-600"
              aria-hidden="true"
            />
            <span
              data-testid="summary-truck-loads"
              className="text-xs font-semibold text-status-info-800"
            >
              {totalTruckLoads.toFixed(2)}
            </span>
            <span className="text-xs text-status-info-600">
              {t('cart:cart.trucks_lowercase')}
            </span>
          </div>
          <div className="flex items-center gap-1 px-2 py-1 bg-warning-50 rounded-md">
            <Package
              size={10}
              className="text-warning-600"
              aria-hidden="true"
            />
            <span
              data-testid="summary-railcar-loads"
              className="text-xs font-semibold text-warning-700"
            >
              {totalRailcarLoads.toFixed(2)}
            </span>
            <span className="text-xs text-warning-600">
              {t('cart:cart.railcars_lowercase')}
            </span>
          </div>
        </div>
      )}
      <NavLink
        to="/cart"
        className="w-full py-3 bg-neut-light text-neut-700 border border-neut-300 rounded-lg hover:bg-neut-50 transition-colors flex items-center justify-center gap-2 font-medium"
      >
        <ShoppingCart className="w-4 h-4" aria-hidden="true" />
        {t('order_builder.cart_summary.go_to_cart')}
      </NavLink>
    </div>
  );
}

type QuantityCardProps = {
  totalQuantity: number;
  targetSize: number;
  unit: string;
  targetLabel: string;
  totalLabel: string;
};

function QuantityCard({
  totalQuantity,
  targetSize,
  unit,
  targetLabel,
  totalLabel,
}: Readonly<QuantityCardProps>) {
  const { t } = useTranslation('order-builder');
  const percentage =
    targetSize > 0 ? Math.min((totalQuantity / targetSize) * 100, 100) : 100;
  const difference = parseFloat(
    Math.abs(totalQuantity - targetSize).toFixed(2)
  );

  return (
    <div className="py-2">
      <div className="flex items-center justify-between text-sm mb-2">
        <span className="text-neut-sec">{totalLabel}:</span>
        <span
          data-testid={`quantity-card-total-${totalLabel}`}
          className="font-bold text-neut-900"
        >
          {parseFloat(totalQuantity.toFixed(2))} {unit}
        </span>
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs">
          <span className="text-neut-500">{targetLabel}:</span>
          <span
            data-testid={`quantity-card-target-${targetLabel}`}
            className="text-neut-700"
          >
            {parseFloat(targetSize.toFixed(2))} {unit}
          </span>
        </div>
        <div className="relative h-1.5 bg-neut-100 rounded-full overflow-hidden">
          <div
            className={`absolute top-0 left-0 h-full transition-all duration-300 ${percentage < 100 ? 'bg-warning-600' : 'bg-success-600'} `}
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
        <div
          className={`flex items-center justify-center text-xs ${percentage < 100 ? 'bg-warning-50' : 'bg-success-50'} px-2 py-1 rounded`}
        >
          <span
            data-testid={`quantity-card-difference-${totalLabel}`}
            className={`font-medium ${percentage < 100 ? 'text-warning-600' : 'text-success-600'}`}
          >
            {percentage < 100
              ? t('order_builder.cart_summary.short_by')
              : t('order_builder.cart_summary.excess')}
            : {difference} {unit}
          </span>
        </div>
      </div>
    </div>
  );
}
